<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { useStoreGetters } from "dashboard/composables/store";

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
});

const route = useRoute();
const router = useRouter();
const getters = useStoreGetters();
const accountId = computed(() => getters.getCurrentAccountId.value);
const currentUser = computed(() => getters.getCurrentUser.value);

const lockInfo = ref(null);
const isLoading = ref(true);
let refreshInterval = null;
let heartbeatInterval = null;

// Lock durumunu kontrol et
const checkLock = async () => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/lock`
    );
    lockInfo.value = response.data;
  } catch (error) {
    console.error("Lock check error:", error);
  } finally {
    isLoading.value = false;
  }
};

// Kilidi al
const acquireLock = async () => {
  try {
    await axios.post(
      `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/lock`
    );
    await checkLock();
    startHeartbeat();
  } catch (error) {
    console.error("Lock acquire error:", error);
    await checkLock();
  }
};

// Kilidi yenile (heartbeat)
const refreshLock = async () => {
  try {
    await axios.post(
      `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/lock/refresh`
    );
  } catch (error) {
    console.error("Lock refresh error:", error);
  }
};

// Kilidi kaldır
const releaseLock = async () => {
  try {
    await axios.delete(
      `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/lock`
    );
  } catch (error) {
    console.error("Lock release error:", error);
  }
};

// Kilidi devral
const takeoverLock = async () => {
  try {
    await axios.post(
      `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/lock/takeover`
    );
    await checkLock();
    startHeartbeat();
  } catch (error) {
    console.error("Lock takeover error:", error);
  }
};

// Listeye geri dön
const goBack = () => {
  window.location.href = "/app/accounts/" + accountId.value + "/contacts";
};

// Heartbeat başlat - her 1 dakikada bir yenile (kilit süresi 2 dakika)
const startHeartbeat = () => {
  if (heartbeatInterval) clearInterval(heartbeatInterval);
  heartbeatInterval = setInterval(refreshLock, 60 * 1000); // 1 dakikada bir
};

// Sayfa kapatılmadan önce kilidi kaldır
const handleBeforeUnload = (event) => {
  if (lockInfo.value?.locked_by_me) {
    // Sync XMLHttpRequest ile kilidi kaldır (beforeunload için tek yol)
    const xhr = new XMLHttpRequest();
    xhr.open("DELETE", `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/lock`, false);
    xhr.setRequestHeader("Content-Type", "application/json");
    try {
      xhr.send();
    } catch (e) {
      // Ignore errors
    }
  }
};

// Visibility change - sekme gizlenince de kilidi taze tut
const handleVisibilityChange = () => {
  if (document.visibilityState === "visible" && lockInfo.value?.locked_by_me) {
    refreshLock();
  }
};

onMounted(async () => {
  await checkLock();
  
  if (!lockInfo.value?.locked || lockInfo.value?.locked_by_me) {
    await acquireLock();
  }
  
  refreshInterval = setInterval(checkLock, 30000);
  window.addEventListener("beforeunload", handleBeforeUnload);
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onBeforeUnmount(() => {
  if (refreshInterval) clearInterval(refreshInterval);
  if (heartbeatInterval) clearInterval(heartbeatInterval);
  window.removeEventListener("beforeunload", handleBeforeUnload);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  
  // Kilidi kaldır (Vue routing ile sayfa değiştiğinde)
  if (lockInfo.value?.locked_by_me) {
    releaseLock();
  }
});

watch(() => props.contactId, async () => {
  isLoading.value = true;
  await checkLock();
  if (!lockInfo.value?.locked || lockInfo.value?.locked_by_me) {
    await acquireLock();
  }
});

const isLockedByOther = computed(() => {
  return lockInfo.value?.locked && !lockInfo.value?.locked_by_me;
});

const lockedByName = computed(() => {
  return lockInfo.value?.locked_by?.name || "Bilinmeyen Kullanıcı";
});
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isLockedByOther"
        class="lock-overlay"
      >
        <div class="lock-content">
          <div class="lock-icon">
            🔒
          </div>
          <h2 class="lock-title">Kişi Kartı Düzenleniyor</h2>
          <p class="lock-message">
            Bu kişi kartı ile başka bir kullanıcı ilgileniyor.
          </p>
          <div class="lock-user">
            <span class="lock-user-label">Düzenleyen:</span>
            <span class="lock-user-name">{{ lockedByName }}</span>
          </div>
          <div class="button-group">
            <button 
              class="takeover-button"
              @click="takeoverLock"
            >
              🔓 Devral
            </button>
            <button 
              class="back-button"
              @click="goBack"
            >
              ← Vazgeç
            </button>
          </div>
          <p class="lock-hint">
            Devral butonuna basarsanız, diğer kullanıcının değişiklikleri kaybolabilir.
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.lock-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

.lock-content {
  background: white;
  padding: 40px 48px;
  border-radius: 16px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
}

.lock-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.lock-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.lock-message {
  font-size: 15px;
  color: #64748b;
  margin: 0 0 24px 0;
}

.lock-user {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f1f5f9;
  border-radius: 8px;
  margin-bottom: 24px;
}

.lock-user-label {
  font-size: 13px;
  color: #64748b;
}

.lock-user-name {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.button-group {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.takeover-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.takeover-button:hover {
  background: #2563eb;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.back-button:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
}

.lock-hint {
  font-size: 12px;
  color: #94a3b8;
  margin: 16px 0 0 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
